import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import * as authService from '../services/auth.service';
import { useUIStore } from '../store/uiStore';

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    login: setAuth,
    logout: clearAuth,
    setLoading,
    updateUser,
  } = useAuthStore();

  const { showToast } = useUIStore();

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (tokens?.accessToken && !user) {
        try {
          const { user: fetchedUser } = await authService.getMe();
          updateUser(fetchedUser);
        } catch (error) {
          clearAuth();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [tokens, user, updateUser, clearAuth, setLoading]);

  const requestOTP = useCallback(
    async (identifier: string, type: 'phone' | 'email') => {
      try {
        const result = await authService.requestOTP(identifier, type);
        showToast(result.message, 'success');
        return true;
      } catch (error) {
        showToast('Failed to send OTP. Please try again.', 'error');
        return false;
      }
    },
    [showToast]
  );

  const verifyOTP = useCallback(
    async (identifier: string, otp: string, type: 'phone' | 'email') => {
      try {
        const result = await authService.verifyOTP(identifier, otp, type);
        setAuth(result.user, result.tokens);
        showToast('Login successful!', 'success');

        return { success: true, needsProfileCompletion: false };
      } catch (error) {
        showToast('Invalid OTP. Please try again.', 'error');
        return { success: false, needsProfileCompletion: false };
      }
    },
    [setAuth, showToast]
  );

  // OAuth methods commented out for auth overhaul — easy re-enable
  // const loginWithGoogle = useCallback(...)
  // const loginWithLinkedIn = useCallback(...)
  // const completeProfile = useCallback(...)

  const logout = useCallback(async () => {
    try {
      if (tokens?.refreshToken) {
        await authService.logout(tokens.refreshToken);
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      clearAuth();
      navigate('/');
      showToast('Logged out successfully', 'info');
    }
  }, [tokens, clearAuth, navigate, showToast]);

  return {
    user,
    isAuthenticated,
    isLoading,
    requestOTP,
    verifyOTP,
    logout,
  };
}
